import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useState } from 'react';

interface RankingInputProps {
  options: Record<string, any>[];
  getLabel: (obj: Record<string, any>) => string;
  disabled?: boolean;
  onChange?: (orderedItems: string[]) => void;
}

interface SortableItemProps {
  id: string;
  label: string;
  index: number;
  disabled?: boolean;
}

const SortableItem = ({ id, label, index, disabled }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div className="relative flex items-center gap-4">
      {/* Left Dotted Indicator */}
      <div className="flex flex-col items-center justify-center gap-1 px-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#C0275E] opacity-60" />
        ))}
      </div>

      {/* Card */}
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...(!disabled ? listeners : {})}
        className={`
          flex-1
          px-6 py-4
          border-2 border-[#C0275E]
          rounded-xl
          bg-[#F3E7EC]
          text-[#C0275E]
          text-lg
          transition-all
          select-none
          ${!disabled ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isDragging ? 'shadow-xl scale-[1.02]' : ''}
        `}
      >
        <div className="flex items-center gap-4">
          {/* Rank Number */}
          <span className="font-semibold text-[#C0275E]">{index + 1}.</span>

          {/* Label */}
          <span>{label}</span>
        </div>
      </div>
    </div>
  );
};

export const RankingInput = ({
  options,
  getLabel,
  disabled = false,
  onChange,
}: RankingInputProps) => {
  const [items, setItems] = useState(() =>
    options.map((o, i) => ({
      id: `item-${i}`,
      label: getLabel(o),
    })),
  );

  // If options change dynamically
  useEffect(() => {
    setItems(
      options.map((o, i) => ({
        id: `item-${i}`,
        label: getLabel(o),
      })),
    );
  }, [options]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id);
      const newIndex = prev.findIndex((item) => item.id === over.id);
      const newOrder = arrayMove(prev, oldIndex, newIndex);

      if (onChange) {
        onChange(newOrder.map((item) => item.label));
      }

      return newOrder;
    });
  };

  return (
    <div className="space-y-4">
      {!disabled && (
        <p className="text-xs text-[#621C43] bg-pink-50 p-3 rounded-lg border border-pink-200">
          Drag options to rank them in order of preference.
        </p>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {items.map((item, index) => (
              <SortableItem
                key={item.id}
                id={item.id}
                label={item.label}
                index={index}
                disabled={disabled}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
